import { useMemo } from "react"
import { PaginationState } from "@tanstack/react-table"
import {
  type Query as CubeQuery,
  type TimeDimension,
  type BinaryFilter,
} from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import useDate from "~/ui-rtk/hooks/date"
import { getPivotData } from "~/ui-rtk/utils/cube"
import { MetricsMapping } from "~/ui-rtk/constants/metrics-mapping"

type DeviceCategory = "ALL" | "DESKTOP" | "MOBILE" | "TABLET"

const HOMEPAGE_FUNNEL_CUBE = "company_web_analytics_homepage_funnel"

export interface HomepageFunnelData {
  homepageViewers: number
  shoppingPageViewers: number
  addersToCart: number
  purchasers: number
  homepageToShopConversion: number
  shopToCartConversion: number
  homepageToCartConversion: number
  cartToPurchaseConversion: number
  shopToPurchaseConversion: number
  homepageToPurchaseConversion: number
}

export interface HomepageFunnelTableRow {
  deviceCategory: string
  homepageViewers: number
  shoppingPageViewers: number
  addersToCart: number
  purchasers: number
  homepageToShopConversion: number
  shopToCartConversion: number
  homepageToCartConversion: number
  cartToPurchaseConversion: number
  shopToPurchaseConversion: number
  homepageToPurchaseConversion: number
}

const USE_MOCK_DATA = false // Set to false when cube is ready

// Helper function to calculate conversion rates
const calculateConversionRates = (
  homepageViewers: number,
  shoppingPageViewers: number,
  addersToCart: number,
  purchasers: number,
) => ({
  homepageToShopConversion:
    homepageViewers > 0 ? (shoppingPageViewers / homepageViewers) * 100 : 0,
  shopToCartConversion:
    shoppingPageViewers > 0 ? (addersToCart / shoppingPageViewers) * 100 : 0,
  homepageToCartConversion:
    homepageViewers > 0 ? (addersToCart / homepageViewers) * 100 : 0,
  cartToPurchaseConversion:
    addersToCart > 0 ? (purchasers / addersToCart) * 100 : 0,
  shopToPurchaseConversion:
    shoppingPageViewers > 0 ? (purchasers / shoppingPageViewers) * 100 : 0,
  homepageToPurchaseConversion:
    homepageViewers > 0 ? (purchasers / homepageViewers) * 100 : 0,
})

// Generate mock funnel data
const generateMockFunnelData = (
  deviceCategory: DeviceCategory,
): HomepageFunnelData => {
  const baseMultiplierMap: Record<DeviceCategory, number> = {
    ALL: 1,
    DESKTOP: 0.5,
    MOBILE: 0.35,
    TABLET: 0.15,
  }
  const baseMultiplier = baseMultiplierMap[deviceCategory]

  const homepageViewers = Math.floor(50000 * baseMultiplier)
  const shoppingPageViewers = Math.floor(homepageViewers * 0.65)
  const addersToCart = Math.floor(shoppingPageViewers * 0.35)
  const purchasers = Math.floor(addersToCart * 0.42)

  const conversionRates = calculateConversionRates(
    homepageViewers,
    shoppingPageViewers,
    addersToCart,
    purchasers,
  )

  return {
    homepageViewers,
    shoppingPageViewers,
    addersToCart,
    purchasers,
    ...conversionRates,
  }
}

// Generate mock table data
const generateMockTableData = (
  deviceCategory: DeviceCategory,
): HomepageFunnelTableRow[] => {
  if (deviceCategory === "ALL") {
    return ["Desktop", "Mobile", "Tablet"].map(device => {
      const deviceMultiplierMap: Record<string, number> = {
        Desktop: 0.5,
        Mobile: 0.35,
        Tablet: 0.15,
      }
      const baseMultiplier = deviceMultiplierMap[device] ?? 0.15
      const homepageViewers = Math.floor(50000 * baseMultiplier)
      const shoppingPageViewers = Math.floor(
        homepageViewers * (0.6 + Math.random() * 0.1),
      )
      const addersToCart = Math.floor(
        shoppingPageViewers * (0.3 + Math.random() * 0.1),
      )
      const purchasers = Math.floor(
        addersToCart * (0.38 + Math.random() * 0.08),
      )

      const conversionRates = calculateConversionRates(
        homepageViewers,
        shoppingPageViewers,
        addersToCart,
        purchasers,
      )

      return {
        deviceCategory: device,
        homepageViewers,
        shoppingPageViewers,
        addersToCart,
        purchasers,
        ...conversionRates,
      }
    })
  }

  // For specific device category, return single row
  const deviceLabelMap: Record<string, string> = {
    DESKTOP: "Desktop",
    MOBILE: "Mobile",
    TABLET: "Tablet",
  }
  const deviceLabel = deviceLabelMap[deviceCategory] ?? "Tablet"
  const mockData = generateMockFunnelData(deviceCategory)

  return [
    {
      deviceCategory: deviceLabel,
      ...mockData,
    },
  ]
}

export default function useConnect(
  deviceCategory: DeviceCategory,
  dateRange?: (string | undefined)[],
  paginationState?: PaginationState,
  compareRange?: (string | undefined)[],
  additionalTableFilters?: BinaryFilter[],
) {
  const currentCompany = useCurrentCompany()
  const { dateRange: fallbackDateRange } = useMinMaxDateRange()
  const dayjs = useDate()

  const companyId = currentCompany?.id || ""
  // Use provided dateRange or fall back to useMinMaxDateRange
  const effectiveDateRange =
    dateRange && dateRange[0] && dateRange[1]
      ? [dateRange[0], dateRange[1]]
      : [
          dayjs(fallbackDateRange[0]).format("YYYY-MM-DD"),
          dayjs(fallbackDateRange[1]).format("YYYY-MM-DD"),
        ]
  const startDate = effectiveDateRange[0]
  const endDate = effectiveDateRange[1]

  // Handle comparison date range
  const effectiveCompareRange =
    compareRange && compareRange[0] && compareRange[1]
      ? [compareRange[0], compareRange[1]]
      : undefined
  const compareStartDate = effectiveCompareRange?.[0]
  const compareEndDate = effectiveCompareRange?.[1]
  const isComparing = Boolean(compareStartDate && compareEndDate)

  const isLoading = false

  // ============================================================================
  // MOCK MODE - Return mock data
  // ============================================================================
  if (USE_MOCK_DATA) {
    return {
      isLoading,
      funnelData: generateMockFunnelData(deviceCategory),
      tableData: generateMockTableData(deviceCategory),
      totalCount: generateMockTableData(deviceCategory).length,
    }
  }

  // ============================================================================
  // REAL MODE - Use metrics mapping
  // ============================================================================

  // Build queries from metrics mapping with UI-level filters
  const summaryQuery = useMemo(() => {
    const baseQuery = MetricsMapping.homepage_funnel_summary.q as CubeQuery
    const additionalFilters: BinaryFilter[] = []

    // Add company filter
    additionalFilters.push({
      member: `${HOMEPAGE_FUNNEL_CUBE}.companyid`,
      operator: "equals",
      values: [companyId],
    })

    // Add device_category dimension filter
    if (deviceCategory !== "ALL") {
      additionalFilters.push({
        member: `${HOMEPAGE_FUNNEL_CUBE}.device_category`,
        operator: "equals",
        values: [deviceCategory.toLowerCase()],
      })
    }

    const timeDimensions = baseQuery.timeDimensions?.map(
      (td: TimeDimension) => {
        if (isComparing && compareStartDate && compareEndDate) {
          return {
            ...td,
            compareDateRange: [
              [compareStartDate, compareEndDate],
              [startDate, endDate],
            ],
          }
        }
        return { ...td, dateRange: [startDate, endDate] }
      },
    )

    return {
      ...baseQuery,
      filters: [...(baseQuery.filters || []), ...additionalFilters],
      timeDimensions,
    } as CubeQuery
  }, [
    deviceCategory,
    companyId,
    startDate,
    endDate,
    isComparing,
    compareStartDate,
    compareEndDate,
  ])

  const tableQuery = useMemo(() => {
    const baseQuery = MetricsMapping.homepage_funnel_table.q as CubeQuery
    const filters: BinaryFilter[] = []

    // Add company filter
    filters.push({
      member: `${HOMEPAGE_FUNNEL_CUBE}.companyid`,
      operator: "equals",
      values: [companyId],
    })

    // For table, if a specific device is selected, filter to that device
    if (deviceCategory !== "ALL") {
      filters.push({
        member: `${HOMEPAGE_FUNNEL_CUBE}.device_category`,
        operator: "equals",
        values: [deviceCategory.toLowerCase()],
      })
    }

    if (additionalTableFilters?.length) {
      filters.push(...additionalTableFilters)
    }

    const timeDimensions = baseQuery.timeDimensions?.map(
      (td: TimeDimension) => {
        if (isComparing && compareStartDate && compareEndDate) {
          return {
            ...td,
            compareDateRange: [
              [compareStartDate, compareEndDate],
              [startDate, endDate],
            ],
          }
        }
        return { ...td, dateRange: [startDate, endDate] }
      },
    )

    const query = {
      ...baseQuery,
      filters: [...(baseQuery.filters || []), ...filters],
      timeDimensions,
    } as CubeQuery

    if (paginationState) {
      query.limit = paginationState.pageSize
      query.offset = paginationState.pageIndex * paginationState.pageSize
    }

    return query
  }, [
    deviceCategory,
    companyId,
    startDate,
    endDate,
    paginationState,
    isComparing,
    compareStartDate,
    compareEndDate,
    additionalTableFilters,
  ])

  const countQuery = useMemo(() => {
    const filters: BinaryFilter[] = []

    // Add company filter
    filters.push({
      member: `${HOMEPAGE_FUNNEL_CUBE}.companyid`,
      operator: "equals",
      values: [companyId],
    })

    // Add device_category dimension filter if not ALL
    if (deviceCategory !== "ALL") {
      filters.push({
        member: `${HOMEPAGE_FUNNEL_CUBE}.device_category`,
        operator: "equals",
        values: [deviceCategory.toLowerCase()],
      })
    }

    if (additionalTableFilters?.length) {
      filters.push(...additionalTableFilters)
    }

    return {
      dimensions: [`${HOMEPAGE_FUNNEL_CUBE}.device_category`],
      filters,
      timeDimensions: [
        {
          dimension: `${HOMEPAGE_FUNNEL_CUBE}.md_date`,
          dateRange: [startDate, endDate],
        },
      ],
    } as CubeQuery
  }, [deviceCategory, companyId, startDate, endDate, additionalTableFilters])

  // Fetch data
  const {
    resultSet: summaryResultSet,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useCubeQuery(summaryQuery)
  const {
    resultSet: tableResultSet,
    isLoading: isTableLoading,
    error: tableError,
  } = useCubeQuery(tableQuery)
  const { resultSet: countResultSet, isLoading: isCountLoading } =
    useCubeQuery(countQuery)

  // Helper function to process funnel data from raw data array
  const processFunnelData = (data: Array<Record<string, unknown>>) => {
    const totals = data.reduce(
      (
        acc: {
          homepageViewers: number
          shoppingPageViewers: number
          addersToCart: number
          purchasers: number
        },
        row: Record<string, unknown>,
      ) => ({
        homepageViewers:
          acc.homepageViewers +
          Number(row[`${HOMEPAGE_FUNNEL_CUBE}.homepage_viewers`] || 0),
        shoppingPageViewers:
          acc.shoppingPageViewers +
          Number(row[`${HOMEPAGE_FUNNEL_CUBE}.shopping_page_viewers`] || 0),
        addersToCart:
          acc.addersToCart +
          Number(row[`${HOMEPAGE_FUNNEL_CUBE}.adders_to_cart`] || 0),
        purchasers:
          acc.purchasers +
          Number(row[`${HOMEPAGE_FUNNEL_CUBE}.purchasers`] || 0),
      }),
      {
        homepageViewers: 0,
        shoppingPageViewers: 0,
        addersToCart: 0,
        purchasers: 0,
      },
    )

    const conversionRates = calculateConversionRates(
      totals.homepageViewers,
      totals.shoppingPageViewers,
      totals.addersToCart,
      totals.purchasers,
    )

    return {
      homepageViewers: totals.homepageViewers,
      shoppingPageViewers: totals.shoppingPageViewers,
      addersToCart: totals.addersToCart,
      purchasers: totals.purchasers,
      ...conversionRates,
    }
  }

  // Process summary data
  const funnelData = useMemo<HomepageFunnelData | null>(() => {
    if (!summaryResultSet) return null

    // When using compareDateRange, access loadResponses
    const resultSetWithResponses = summaryResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      isComparing &&
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 1
    ) {
      // loadResponses[1] = current period data
      const currentData = resultSetWithResponses.loadResponses[1]?.data || []
      return processFunnelData(currentData)
    }

    // Non-comparison mode - use regular pivot data
    const pivotData = getPivotData(summaryResultSet, summaryQuery)
    return processFunnelData(pivotData)
  }, [summaryResultSet, summaryQuery, isComparing])

  // Process comparison summary data (from loadResponses[0])
  const compareFunnelData = useMemo<HomepageFunnelData | null>(() => {
    if (!isComparing || !summaryResultSet) {
      return null
    }

    // When using compareDateRange, access loadResponses
    const resultSetWithResponses = summaryResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 0
    ) {
      // loadResponses[0] = comparison period data
      const comparisonData = resultSetWithResponses.loadResponses[0]?.data || []
      return processFunnelData(comparisonData)
    }

    return null
  }, [isComparing, summaryResultSet])

  // Helper function to process table row data
  const processTableRow = (
    row: Record<string, unknown>,
  ): HomepageFunnelTableRow => {
    const homepageViewers = Number(
      row[`${HOMEPAGE_FUNNEL_CUBE}.homepage_viewers`] || 0,
    )
    const shoppingPageViewers = Number(
      row[`${HOMEPAGE_FUNNEL_CUBE}.shopping_page_viewers`] || 0,
    )
    const addersToCart = Number(
      row[`${HOMEPAGE_FUNNEL_CUBE}.adders_to_cart`] || 0,
    )
    const purchasers = Number(row[`${HOMEPAGE_FUNNEL_CUBE}.purchasers`] || 0)

    const conversionRates = calculateConversionRates(
      homepageViewers,
      shoppingPageViewers,
      addersToCart,
      purchasers,
    )

    const rawDevice = String(
      row[`${HOMEPAGE_FUNNEL_CUBE}.device_category`] || "unknown",
    )
    const deviceCategory =
      rawDevice.charAt(0).toUpperCase() + rawDevice.slice(1).toLowerCase()

    return {
      deviceCategory,
      homepageViewers,
      shoppingPageViewers,
      addersToCart,
      purchasers,
      ...conversionRates,
    }
  }

  // Process table data
  const tableData = useMemo<HomepageFunnelTableRow[]>(() => {
    if (!tableResultSet) return []

    const resultSetWithResponses = tableResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      isComparing &&
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 0
    ) {
      // Two responses: [0]=compare, [1]=current. One response: use [0] as current.
      const current =
        resultSetWithResponses.loadResponses[1]?.data ??
        resultSetWithResponses.loadResponses[0]?.data ??
        []
      return current.map(row => processTableRow(row))
    }

    const pivotData = getPivotData(tableResultSet, tableQuery)
    return pivotData.map((row: Record<string, unknown>) => processTableRow(row))
  }, [tableResultSet, tableQuery, isComparing])

  const totalCount = useMemo(() => {
    if (!countResultSet) return 0
    const pivotData = getPivotData(countResultSet, countQuery)
    return pivotData.length
  }, [countResultSet, countQuery])

  // Process comparison table data (loadResponses[0] = compare period)
  const compareTableData = useMemo<HomepageFunnelTableRow[] | undefined>(() => {
    if (!isComparing || !tableResultSet) return undefined

    const resultSetWithResponses = tableResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 1
    ) {
      const comparisonData =
        resultSetWithResponses.loadResponses[0]?.data ??
        ([] as Array<Record<string, unknown>>)
      return comparisonData.map((row: Record<string, unknown>) =>
        processTableRow(row),
      )
    }

    return undefined
  }, [isComparing, tableResultSet])

  return {
    isLoading: isSummaryLoading || isTableLoading || isCountLoading,
    error: summaryError || tableError,
    funnelData,
    compareFunnelData,
    tableData,
    compareTableData,
    totalCount,
    /** The exact request behind `tableData` — the CSV export re-runs it as-is. */
    tableQuery,
  }
}
